import type { AdminAudienceFilterSelection, OperationalMetric } from "../../api/types";
import { formatCount, isValidCount } from "../../formatting/number";
import { formatDateTime } from "../../formatting/date";
import { isTerminalLifecycleState } from "../../formatting/status";

/**
 * Pure, client-side derivations for the single-page campaign detail
 * (REDISENO-PANEL.md §5.3/§9.4). No I/O, no React — same "Extract-Before-Mock"
 * convention as `scheduleValidation.ts`/`consequentialActions.ts`/`campaignSort.ts`
 * (and `features/overview/derived.ts`, which this intentionally does NOT
 * import from — campaigns and overview stay independent feature slices; a
 * couple of tiny helpers (relative day label, metric lookup) are duplicated
 * here rather than creating a cross-feature dependency).
 */

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDayMs(ms: number): number {
  const date = new Date(ms);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

/** Same calendar-day relative label convention as `features/overview/derived.ts#relativeDayLabel`. */
export function relativeDayLabel(targetIso: string, nowMs: number): string {
  const target = new Date(targetIso).getTime();
  if (Number.isNaN(target)) return "sin fecha";
  const days = Math.round((startOfDayMs(target) - startOfDayMs(nowMs)) / DAY_MS);
  if (days === 0) return "hoy";
  if (days === 1) return "mañana";
  if (days === -1) return "ayer";
  return days > 0 ? `en ${days} días` : `hace ${-days} días`;
}

/** Finds `key` in a metric catalog and returns its value only when `availability === "available"` (§9.2: never surface a value for a blocked/unavailable metric). A negative or NaN/non-finite value is treated as invalid too — a count can never legitimately be negative. */
export function metricValue(metrics: readonly OperationalMetric[], key: string): number | null {
  const found = metrics.find((metric) => metric.key === key);
  if (!found || found.availability !== "available" || !isValidCount(found.value) || found.value < 0) return null;
  return found.value;
}

/** `true` only for a non-empty, parsable ISO-8601 instant — guards `statusLineFor` against an empty/unparsable `fechaProgramada` before it reaches `formatDateTime` (which would otherwise render "Invalid Date"). */
function hasValidFechaProgramada(fechaProgramada: string): boolean {
  return Boolean(fechaProgramada) && !Number.isNaN(new Date(fechaProgramada).getTime());
}

/** §5.3 header "frase de estado", one sentence per lifecycle state, exact copy. */
export function statusLineFor(estado: string, fechaProgramada: string, nowMs: number): string {
  switch (estado) {
    case "borrador":
      // Codex finding (PR #384 round 5): unlike every other state below,
      // this used a plain truthiness check instead of
      // `hasValidFechaProgramada` — a non-empty but unparsable
      // `fechaProgramada` is truthy, so it still reached `formatDateTime`,
      // which throws a `RangeError` on an invalid `Date` and crashes the
      // whole campaign detail screen.
      return hasValidFechaProgramada(fechaProgramada)
        ? `Borrador · envío tentativo ${formatDateTime(fechaProgramada)}. Prográmala para que salga automáticamente.`
        : "Borrador sin fecha de envío.";
    case "programada":
      return hasValidFechaProgramada(fechaProgramada)
        ? `Saldrá automáticamente ${relativeDayLabel(fechaProgramada, nowMs)}, ${formatDateTime(fechaProgramada)}.`
        : "Programada, sin fecha de envío.";
    case "enviando":
      return hasValidFechaProgramada(fechaProgramada) ? `Enviando desde ${formatDateTime(fechaProgramada)}.` : "Enviando.";
    case "completada":
      return hasValidFechaProgramada(fechaProgramada) ? `Enviada el ${formatDateTime(fechaProgramada)}.` : "Enviada.";
    case "parcial":
      return hasValidFechaProgramada(fechaProgramada)
        ? `Enviada el ${formatDateTime(fechaProgramada)}; algunos correos no se entregaron.`
        : "Enviada; algunos correos no se entregaron.";
    case "fallida":
      return hasValidFechaProgramada(fechaProgramada)
        ? `El envío del ${formatDateTime(fechaProgramada)} no se pudo completar.`
        : "El envío no se pudo completar.";
    case "cancelada":
      return "Cancelada; no se envió ningún correo.";
    default:
      return "";
  }
}

/**
 * §5.3 "Progreso del envío", `encolados === 0` branch title.
 *
 * Remaining Codex PR #384 follow-up finding 8: a TERMINAL state (completada/
 * fallida — `isTerminalLifecycleState`, the same shared definition
 * `CampaignDetailScreen` already uses for its own polling, which deliberately
 * excludes `parcial`) that still ended up with zero `encolados` never sent
 * anything and never will. The default "Todavía no se ha enviado" copy below
 * wrongly implies a send is still pending for that case.
 */
export function waitTitleFor(estado: string, fechaProgramada: string): string {
  if (estado === "cancelada") return "Esta campaña se canceló";
  // Codex finding (PR #384 round 5): this formatted `fechaProgramada`
  // unconditionally, with no `hasValidFechaProgramada` guard at all — a
  // scheduled campaign with zero queued messages and an unparsable date
  // threw a `RangeError` and crashed the campaign detail, the same
  // unchecked-formatting bug `statusLineFor`'s `borrador` branch had.
  if (estado === "programada" && hasValidFechaProgramada(fechaProgramada)) {
    return `Programada para ${formatDateTime(fechaProgramada)}`;
  }
  // native review finding R3-002: `fallida` with zero encolados can fail for
  // many reasons (a dispatch/infrastructure error before anything was ever
  // enqueued, for example), not necessarily because the audience was empty.
  // `completada` (the only other terminal state reaching this branch) is the
  // one case that CAN honestly claim that: it finished successfully with
  // nothing left to do, so an empty audience is the only explanation left.
  if (estado === "fallida") return "El envío no se pudo completar.";
  if (isTerminalLifecycleState(estado)) return "No había contactos a los que enviar.";
  return "Todavía no se ha enviado";
}

/** §5.2/§5.3 singular-at-one convention for an estimated audience count; `undefined` while the preview total is still loading. */
export function audienceCountLabel(total: number | undefined): string {
  if (total === undefined) return "calculando…";
  return total === 1 ? "≈ 1 contacto activo" : `≈ ${formatCount(total)} contactos activos`;
}

/**
 * Codex PR #384 follow-up finding 2: a legacy Segment campaign (audience-
 * filter GET 404, `CampaignDetailScreen`'s own `audienceFilterNotFound`) has
 * no saved filter of its own to preview — the audience preview query never
 * runs for it (see `CampaignDetailScreen`'s `previewQuery.enabled`), so
 * `total` stays `undefined` forever. `audienceCountLabel`'s "calculando…"
 * would then be permanently, misleadingly stuck in a "still loading"
 * phrasing. `definedBySegment` picks this distinct, permanent label instead
 * — used by the Audiencia card and both Enviar ahora/Programar envío
 * dialogs so none of them shows a misleading ≈N for this case.
 */
export function audienceSummaryLabel(total: number | undefined, definedBySegment: boolean): string {
  if (definedBySegment) return "Audiencia definida por el segmento";
  return audienceCountLabel(total);
}

/**
 * §5.3 "Progreso del envío", `encolados === 0` branch body text.
 * `definedBySegment` mirrors `audienceSummaryLabel` above, for the same
 * legacy-Segment reason (default `false` keeps every existing call site
 * unchanged).
 */
export function waitTextFor(estado: string, total: number | undefined, definedBySegment = false): string {
  if (estado === "cancelada") return "No se envió ningún correo.";
  // native review finding R3-002: an empty body here left NO failure hint at
  // all for `fallida` — this states the one fact zero encolados DOES
  // establish (no email ever went out), without claiming why, same as
  // `cancelada`'s own body above.
  if (estado === "fallida") return "No se envió ningún correo.";
  if (isTerminalLifecycleState(estado)) return "";
  if (definedBySegment) return "Llegará a la audiencia definida por el segmento asignado.";
  return `Llegará a ${audienceCountLabel(total)} según su audiencia actual.`;
}

/** §9.4 "Pendientes = max(0, encolados − entregado − fallidos)" — "—" (null) whenever `entregado` itself is unavailable, or any input is NaN/non-finite, never a fabricated figure. */
export function pendingCount(encolados: number, entregado: number | null, fallidos: number): number | null {
  if (entregado === null) return null;
  if (!isValidCount(encolados) || !isValidCount(entregado) || !isValidCount(fallidos)) return null;
  return Math.max(0, encolados - entregado - fallidos);
}

/** §9.2: "—" for an unavailable/null figure, never a fabricated 0. */
export function formatOrDash(value: number | null): string {
  return value === null ? "—" : formatCount(value);
}

/** §5.3 "X de Y correos entregados" — "—" in place of X while `entregado` is unavailable, or either input is NaN/non-finite. */
export function formatDeliveredHeadline(entregado: number | null, encolados: number): string {
  const safeEntregado = entregado !== null && isValidCount(entregado) ? entregado : null;
  const encoladosText = isValidCount(encolados) ? formatCount(encolados) : "—";
  return `${formatOrDash(safeEntregado)} de ${encoladosText} correos entregados`;
}

/** progress-bar-sent: "X de Y correos enviados" (sending pace) — "—" in place of X while `enviados` is unavailable or invalid. */
export function formatSentHeadline(enviados: number | null, encolados: number): string {
  const safeEnviados = enviados !== null && isValidCount(enviados) ? enviados : null;
  const encoladosText = isValidCount(encolados) ? formatCount(encolados) : "—";
  return `${formatOrDash(safeEnviados)} de ${encoladosText} correos enviados`;
}

const PERCENT_FORMATTER = new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 });

/** §5.3 big percentage figure (entregados ÷ encolados) — "—" while `entregado` is unavailable, `encolados` is 0, or either input is NaN/non-finite; clamped to [0, 100] so a transient `entregado > encolados` reading never renders over 100%. */
export function formatDeliveredPercent(entregado: number | null, encolados: number): string {
  if (entregado === null || !isValidCount(entregado) || !isValidCount(encolados) || encolados <= 0) return "—";
  const percent = Math.min(100, Math.max(0, (entregado / encolados) * 100));
  return `${PERCENT_FORMATTER.format(percent)}%`;
}

/** §5.3 Audiencia card "País · Cargo · Categoría" compound row — "Sin restricción" only once every one of the three is null. */
export function formatPaisCargoCategoria(
  paisEmpresa: string | null,
  cargo: string | null,
  categoria: string | null,
): string {
  const parts = [paisEmpresa, cargo, categoria].filter((value): value is string => Boolean(value && value.trim()));
  return parts.length > 0 ? parts.join(" · ") : "Sin restricción";
}

/** §5.3 Audiencia card "Empresas" row. */
export function formatEmpresas(empresas: readonly string[] | null | undefined): string {
  return empresas && empresas.length > 0 ? empresas.join(", ") : "Todas";
}

/**
 * Codex finding 2 (PR #384): a saved filter that predates the Empresa
 * multi-select carries only the scalar `empresa`, with `empresas` null —
 * without this, the detail screen's Audiencia card and its own "Editar
 * campaña" modal both silently dropped that legacy company, showing/
 * pre-selecting none instead of the one actually saved. `empresas` wins
 * whenever present (even an empty array, meaning "no restriction") — same
 * precedence the backend and `AudienceFilterEditor` already use (see
 * `api/types.ts`'s own doc comment on `AdminAudienceFilterSelection`); this
 * file intentionally duplicates that small hydration instead of importing
 * from `AudienceFilterEditor` (see this file's own docstring on staying
 * I/O-free and dependency-free).
 */
export function resolveEmpresas(
  selection: Pick<AdminAudienceFilterSelection, "empresas" | "empresa">,
): string[] {
  if (selection.empresas !== null && selection.empresas !== undefined) return selection.empresas;
  return selection.empresa ? [selection.empresa] : [];
}

/**
 * Codex finding 3 (PR #384): a legacy draft whose audience-filter GET 404s
 * schedules via its Segment — saving the Editar campaña modal must never PUT
 * an all-empty selection just because the operator opened and re-saved the
 * form without touching any audience criterion, or that silent PUT would
 * create a real (empty) filter row that permanently REPLACES the Segment
 * fallback with "no restriction" instead of leaving it alone.
 */
export function isEmptyAudienceSelection(selection: AdminAudienceFilterSelection): boolean {
  return (
    selection.showId === null &&
    selection.paisEmpresa === null &&
    resolveEmpresas(selection).length === 0 &&
    selection.cargo === null &&
    selection.categoria === null
  );
}

/** Lifecycle-aware control visibility — mirrors the exact states the authoritative write endpoints enforce server-side, same contract the pre-merge `CampaignSummaryTab.tsx` used. */
export function lifecycleControls(estado: string): {
  canEdit: boolean;
  canSchedule: boolean;
  canCancel: boolean;
  canDispatch: boolean;
} {
  return {
    canEdit: estado === "borrador",
    canSchedule: estado === "borrador",
    canCancel: estado === "borrador" || estado === "programada",
    canDispatch: estado === "programada",
  };
}
